import { useState } from 'react';
import { motion } from 'motion/react';
import { HardHat, Plus, Pencil, Trash2, Eye, EyeOff } from 'lucide-react';
import { useApp } from '@/store';
import { SectionId } from '@/lib/section-permissions';
import { useSectionRights } from '@/lib/use-section-rights';
import { WorkerRole, WORKER_ROLE_TO_API, type Worker } from '@/data/types';
import { apiFetch, ApiError } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { useConfirm } from '@/components/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

type Form = {
  name: string;
  email: string;
  password: string;
  role: WorkerRole;
  active: boolean;
};
const blank = (): Form => ({
  name: '',
  email: '',
  password: '',
  role: WorkerRole.TECHNICIAN,
  active: true,
});

export function Workers() {
  const t = useT();
  const confirm = useConfirm();
  const workers = useApp(s => s.workers);
  const upsertWorker = useApp(s => s.upsertWorker);
  const deleteWorker = useApp(s => s.deleteWorker);
  const toast = useApp(s => s.toast);
  const tenantId = useApp(s => s.currentUser?.tenantId);
  // Writes here go through the org_admin-only users API as well, so the
  // server floor caps every non-admin at Lesen (#95).
  const can = useSectionRights()[SectionId.WORKERS];

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Worker | null>(null);
  const [form, setForm] = useState<Form>(blank());
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const openCreate = () => {
    setEditing(null);
    setForm(blank());
    setShowPw(false);
    setOpen(true);
  };
  const openEdit = (w: Worker) => {
    setEditing(w);
    setForm({
      name: w.name,
      email: w.email ?? '',
      password: '',
      role: w.role,
      active: w.active,
    });
    setShowPw(false);
    setOpen(true);
  };
  const handleSave = async () => {
    const name = form.name.trim();
    const email = form.email.trim().toLowerCase();
    if (!name) return;
    if (!tenantId) {
      toast(t('workers.noTenant'));
      return;
    }
    if (!editing && !email) {
      toast(t('workers.emailRequired'));
      return;
    }
    const pwRequired = !editing;
    if (pwRequired && form.password.length < 8) {
      toast(t('workers.passwordTooShort'));
      return;
    }
    if (!pwRequired && form.password && form.password.length < 8) {
      toast(t('workers.passwordTooShort'));
      return;
    }
    const role = WORKER_ROLE_TO_API[form.role];
    setBusy(true);
    try {
      if (!editing) {
        await apiFetch(`/tenants/${tenantId}/users`, {
          method: 'POST',
          body: JSON.stringify({
            email,
            password: form.password,
            displayName: name,
            role,
          }),
        });
      } else {
        const userId = editing.email ?? editing.id;
        await apiFetch(
          `/tenants/${tenantId}/users/${encodeURIComponent(userId)}`,
          {
            method: 'PUT',
            body: JSON.stringify({
              role,
              displayName: name,
              active: form.active,
              ...(form.password ? { password: form.password } : {}),
            }),
          }
        );
      }
      upsertWorker({
        id: editing?.id ?? email,
        name,
        email: editing?.email ?? email,
        role: form.role,
        active: form.active,
      });
      toast(editing ? t('common.save') : t('workers.newWorker'));
      setOpen(false);
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t('workers.saveFailed'));
    } finally {
      setBusy(false);
    }
  };
  const handleDelete = async (w: Worker) => {
    if (!(await confirm(`${t('common.delete')} ${w.name}?`))) return;
    const userId = w.email ?? w.id;
    if (tenantId && userId) {
      try {
        await apiFetch(
          `/tenants/${tenantId}/users/${encodeURIComponent(userId)}`,
          { method: 'DELETE' }
        );
      } catch (err) {
        // 404 → login account already gone; still drop the worker doc.
        if (!(err instanceof ApiError) || err.status !== 404) {
          toast(
            err instanceof ApiError ? err.message : t('workers.deleteFailed')
          );
          return;
        }
      }
    }
    deleteWorker(w.id);
    toast(t('common.delete'));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <HardHat className="h-6 w-6 text-primary" /> {t('workers.title')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('workers.subtitle')}
          </p>
        </div>
        {can.create && (
          <Button size="sm" onClick={openCreate} data-focus="workers-new">
            <Plus className="h-4 w-4" />
            {t('workers.newWorker')}
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">
            {t('workers.team')} ({workers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('table.avatar')}</TableHead>
                <TableHead>{t('table.name')}</TableHead>
                <TableHead>{t('table.role')}</TableHead>
                <TableHead>{t('table.status')}</TableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {workers.map(w => {
                const initials = w.name
                  .split(' ')
                  .map(n => n[0])
                  .slice(0, 2)
                  .join('');
                return (
                  <TableRow key={w.id}>
                    <TableCell>
                      <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                        {initials}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium">{w.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {w.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={w.active ? 'success' : 'outline'}>
                        {w.active ? t('workers.active') : t('workers.inactive')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {can.edit && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-11 w-11 sm:h-7 sm:w-7"
                            title={t('common.edit')}
                            aria-label={t('common.edit')}
                            onClick={() => openEdit(w)}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                        )}
                        {can.delete && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-11 w-11 sm:h-7 sm:w-7"
                            title={t('common.delete')}
                            aria-label={t('common.delete')}
                            onClick={() => handleDelete(w)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing ? t('common.edit') : t('workers.newWorker')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t('table.name')}</Label>
              <Input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div>
              <Label>{t('common.email')}</Label>
              <Input
                type="email"
                autoComplete="off"
                value={form.email}
                readOnly={!!editing}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              />
            </div>
            <div>
              <Label>{t('workers.password')}</Label>
              <div className="relative">
                <Input
                  type={showPw ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={form.password}
                  placeholder={editing ? t('workers.passwordKeep') : undefined}
                  onChange={e =>
                    setForm(f => ({ ...f, password: e.target.value }))
                  }
                  className="pr-11"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(v => !v)}
                  aria-label={showPw ? t('auth.hidePw') : t('auth.showPw')}
                  className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  {showPw ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
            <div>
              <Label>{t('table.role')}</Label>
              <Select
                value={form.role}
                onValueChange={v =>
                  setForm(f => ({ ...f, role: v as WorkerRole }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={WorkerRole.ADMIN} className="capitalize">
                    {WorkerRole.ADMIN}
                  </SelectItem>
                  <SelectItem value={WorkerRole.STAFF} className="capitalize">
                    {WorkerRole.STAFF}
                  </SelectItem>
                  <SelectItem
                    value={WorkerRole.TECHNICIAN}
                    className="capitalize"
                  >
                    {WorkerRole.TECHNICIAN}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.active}
                onChange={e =>
                  setForm(f => ({ ...f, active: e.target.checked }))
                }
                className="h-4 w-4 accent-primary"
              />
              <span className="text-sm">{t('workers.active')}</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSave} disabled={busy}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
